/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import ContentFrame from '../../../../applicationFrame/ContentFrame';
import { ScrollAwareContext, ScrollAwareScroller, ScrollAwareAppNavStep } from '../../../../components/ScrollAware';
import { AppHeaderScrollableText } from '../../../../components/appNav/AppHeaderScrollable';

import InputWithLabel from '../../../../components/InputWithLabel';
import PreformattedText from '../../../../components/PreformattedText';

import { actions as ownerActions } from '../../../../redux/ducks/owners';
import { actions as systemModalActions } from '../../../../redux/ducks/systemModal';
import { getActiveProfile } from '../../../redux/reduxSelectors';
import NavigationActions from '../../../redux/NavigationActions';

import RouteNames from '../../../RouteNames';
import { validateEmail } from '../../../../../utilities/Validators';
import ContentWrapper from '../../../../components/ContentWrapper';
import Card, { EmptyCard, CardSection } from '../../../../components/card/Card';
import ApiErrorModel from '../../../../../models/ApiErrorModel';
import { ProfileThemeedWarningModal } from '../../../../components/modal/WarningModal';

function mapStateToProps(state, routerProps) {
  const history = routerProps.history;
  const profile = getActiveProfile(state);

  return {
    profileName: profile.greetingName(),
    profile,
    isDependent: profile.isDependent(),
    inviteSelf: routerProps.inviteSelf,
    goBack: () => {
      NavigationActions.goBack(history, RouteNames.profileAccess());
    },
  };
}

function mapDispatchToProps(dispatch) {
  return {
    submitEmail: (email, profile, isPrimary) => {
      return dispatch(ownerActions.inviteOwner(email, profile.data, isPrimary));
    },
    showModal: (content, modalProps) => {
      dispatch(systemModalActions.show(content, modalProps));
    },
  };
}

interface Props {
  profile: any;
  isDependent: boolean;
  submitEmail(email, profile, isPrimary): Promise<any>;
  goBack(): void;
  profileName: string;
  inviteSelf: boolean;
  showModal(content, modalProps): void;
}

interface State {
  email: string;
  isEmailValid: boolean;
  errorMessage: any;
  submitAttemptCount: number;
}

class ViewProfileInviteOwner extends React.Component<Props, State> {

  state: State = {
    email: '',
    isEmailValid: false,
    errorMessage: '',
    submitAttemptCount: 0,
  };

  onChangeEmail = (value) => {
    this.setState({
      email: value,
      isEmailValid: validateEmail(value),
    });
  }

  onFocus = () => {
    this.setState({
      errorMessage: '',
      submitAttemptCount: 0,
    });
  }

  submitEmail = (e) => {
    e.preventDefault();
    if (!this.state.isEmailValid) {
      this.setState({
        errorMessage: <FormattedMessage id="error.email-validator.invalid-email.message" />,
        submitAttemptCount: this.state.submitAttemptCount + 1,
      });
      return;
    }

    this.props.submitEmail(this.state.email, this.props.profile, this.props.inviteSelf)
    .then(() => {
      this.props.goBack();
    })
    .catch((apiError: ApiErrorModel) => {
      const { inviteSelf, showModal, profileName } = this.props;

      if (apiError.isForbidden()) {
        if (inviteSelf) {
          showModal(
            <ProfileThemeedWarningModal
              hasCloseButton={false}
              phraseMessages={{
                title: { id: 'accessEmailExists.title.text' },
                subheading: { id: 'accessEmailExists.heading.text' },
                body: { id: 'accessEmailExists.description.text', values: { string1: profileName, string2: profileName } },
                button: { id: 'accessEmailExists.done.button.label' },
              }}
              onConfirm={this.props.goBack}
              onDismiss={this.props.goBack}
            />,
            {
              onDismiss: this.props.goBack,
            });
        }
      } else if (apiError.isConflict()) {
        this.props.goBack();
      }
    });
  }

  render() {
    const tilteMsg = this.props.inviteSelf ? {
      id: 'accessInviteUserEmail.inviteOwnProfile.title.text',
      values: {
        string1: this.props.profileName,
      },
    } : {
      id: 'accessInviteUserEmail.inviteUser.title.text',
    };
    const accessInviteUserEmail = this.props.isDependent
      ? 'accessInviteUserEmail.notUserProfile.email.message.pre'
      : 'accessInviteUserEmail.userProfile.email.message.pre';

    return (
      <div>
        <ScrollAwareContext>
          <ScrollAwareAppNavStep
            submitDisabled={!this.state.isEmailValid}
            invite={this.submitEmail}
            cancel={this.props.goBack}
            color="white"
          />

          <ContentFrame>
            <ScrollAwareScroller>
              <AppHeaderScrollableText {...tilteMsg} />

              <ContentWrapper>

              <Card>
                <CardSection hasBorder={false}>
                  <form onSubmit={this.submitEmail}>
                    <InputWithLabel
                      value={this.state.email}
                      label={<FormattedMessage id="accessInviteUserEmail.email.field.label" />}
                      onFocus={this.onFocus}
                      onChange={(e) => { this.onChangeEmail(e.target.value); }}
                      invalidHint={this.state.errorMessage}
                      isInvalid={!this.state.isEmailValid && this.state.submitAttemptCount > 0}
                      clearValue={() => { this.onChangeEmail(''); }}
                      type="email"
                      name="email"
                      attemptsBeforeInvalidHint={0}
                      initialFocus={false}
                    />
                  </form>
                </CardSection>
              </Card>

              <EmptyCard style={{ marginTop: 24 }}>
                <PreformattedText
                  className="font-color-gsMedDark"
                  text={accessInviteUserEmail}
                  textValues={{
                    string1: this.props.profileName,
                    string2: this.props.profileName,
                    string3: this.props.profileName,
                  }}
                />
              </EmptyCard>
              </ContentWrapper>
            </ScrollAwareScroller>
          </ContentFrame>
        </ScrollAwareContext>
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(ViewProfileInviteOwner);
